import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { ChevronRight, ChevronDown, Home, FileText, Cpu, ClipboardPen, Settings, Shield, Code, ArrowDownNarrowWide, ArrowUpNarrowWide, Navigation, Search, X, PanelLeftClose, PanelLeft, FileChartColumnIncreasing, GanttChart, CircuitBoard, Wrench, Blocks } from 'lucide-react';
import { navigationConfig } from '../../config/navigationConfig';
import { useAuth } from '../../context/AuthContext';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import { usePreferences } from '../../context/PreferencesContext';
import { reportingApi } from '../../services/api';

// Icon mapping for Level 1 sections
const sectionIcons = {
  'home': Home,
  'engineering': CircuitBoard,
  'documentation': FileText,
  'project-management': GanttChart,
  'equipment': Cpu,
  'data-entry': ClipboardPen,
  'configuration': Settings,
  'reports': FileChartColumnIncreasing,
  'integration-dashboard': Blocks,
  'utilities': Wrench,
  'administration': Shield,
  'development': Code
};

// Flatten permission-filtered nav into searchable results with breadcrumb
const flattenNav = (items, breadcrumb = []) => {
  const results = [];
  items.forEach(item => {
    const crumb = [...breadcrumb, item.label];
    if (!item.headerOnly && item.destination) {
      results.push({ ...item, breadcrumb });
    }
    if (item.children) {
      results.push(...flattenNav(item.children, crumb));
    }
  });
  return results;
};

// Bold the matching substring in a label
const highlightMatch = (label, query) => {
  const idx = label.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return label;
  return <>{label.slice(0, idx)}<strong>{label.slice(idx, idx + query.length)}</strong>{label.slice(idx + query.length)}</>;
};

// Helper to find the path to a destination in the nav tree
const findPathToDestination = (items, destination, path = []) => {
  for (const item of items) {
    const currentPath = [...path, item.id];
    if (item.destination === destination) {
      return currentPath;
    }
    if (item.children) {
      const found = findPathToDestination(item.children, destination, currentPath);
      if (found) return found;
    }
  }
  return null;
};

const NavItem = ({ item, level = 1, currentPage, setCurrentPage, expandedItems, toggleExpand, isCollapsed, itemRefs }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.id);
  const isActive = currentPage === item.destination;
  const Icon = level === 1 ? sectionIcons[item.id] : null;
  
  const handleClick = () => {
    if (!item.headerOnly && item.destination) {
      setCurrentPage(item.destination);
      // Expand if has children but never collapse — chevron handles collapse
      if (hasChildren && !isCollapsed && !isExpanded) {
        toggleExpand(item.id);
      }
    } else if (hasChildren && !isCollapsed) {
      toggleExpand(item.id);
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    if (hasChildren && !isCollapsed) toggleExpand(item.id);
  };

  return (
    <div className="stui-nav-item-container">
      <button
        ref={el => { if (itemRefs) itemRefs.current[item.destination] = el; }}
        onClick={handleClick}
        className={`stui-nav-item stui-nav-item-level-${level} ${isActive ? 'active' : ''}`}
        data-tooltip={item.label}
        data-nav-id={item.id}
      >
        <div className="stui-nav-item-content">
          {hasChildren && !isCollapsed && (
            <span className="stui-nav-item-chevron" onClick={handleChevronClick}>
              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          )}
          {!hasChildren && level > 1 && !isCollapsed && <span className="stui-nav-item-spacer" />}
          {Icon && <Icon size={16} className="stui-nav-item-icon" />}
          {!isCollapsed && (
            <>
              <span className="stui-nav-item-label">
                {item.label}
              </span>
              {item.future && <span className="stui-nav-future-badge">FUTURE</span>}
            </>
          )}
        </div>
      </button>
      
      {hasChildren && isExpanded && !isCollapsed && (
        <div className="stui-nav-children">
          {item.children.map(child => (
            <NavItem
              key={child.id}
              item={child}
              level={level + 1}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              isCollapsed={isCollapsed}
              itemRefs={itemRefs}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Data entry module permission key map: nav item id → AppSec permission key
// Post 2026-08-05 flatten: dataentry.* prefix replaces module.*
const modulePermMap = {
  'estimating':        'dataentry.estimating',
  'integration':       'dataentry.integration',
  'submittals':        'dataentry.submittals',
  'sub-contractors':   'dataentry.subcontractor',
  'purchasing':        'dataentry.purchasing',
  'qaqc':              'dataentry.qaqc',
  'material-transfer': 'dataentry.materialtransfer',
  'commissioning':     'dataentry.commissioning'
};

// Integration tool nav id → AppSec permission key. Used by the Sidebar
// to per-child-filter under integration-dashboard so users only see the
// tools they can invoke. Backend still gates the endpoints; this is UI.
const integrationPermMap = {
  'io-buffer-generation':       'integration.iobuffer',
  'udt-comment-generator':      'integration.udtcomment',
  'scada-tag-generation':       'integration.scadatagen',
  'simulation-tag-generation':  'integration.simtagen',
  'plc-platforms':              'integration.plcplatforms',
  'plc-tag-xref':               'integration.tagxref'
};

const Sidebar = ({ currentPage, setCurrentPage, sidebarTarget, clearSidebarTarget }) => {
  const { isSysAdmin, isAdmin, hasPermission, currentUser } = useAuth();
  const dbContext = useDatabaseSafe();
  const dbSlug = dbContext?.dbSlug || '';
  const [expandedItems, setExpandedItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pmd-nav-expanded') || '[]'); } catch { return []; }
  });
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('pmd-nav-collapsed') === 'true';
  });
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCurrentVisible, setIsCurrentVisible] = useState(false);
  const searchInputRef = useRef(null);
  const itemRefs = useRef({});
  const navRef = useRef(null);

  // ── Server-backed nav state (general.sidebarNavState) ─────────────────────
  // localStorage keys stay as the boot cache (lazy initializers above read them
  // pre-hydration); the preferences server row is the authority once hydrated.
  const { hydrated: prefsHydrated, getPref, setPref } = usePreferences();
  const navPrefAdopted = useRef(false);

  useEffect(() => {
    if (!prefsHydrated || navPrefAdopted.current) return;
    navPrefAdopted.current = true;
    try {
      const v = JSON.parse(getPref('general.sidebarNavState') || '{}');
      if (Array.isArray(v.expanded)) {
        setExpandedItems(v.expanded);
        localStorage.setItem('pmd-nav-expanded', JSON.stringify(v.expanded));
      }
      if (typeof v.collapsed === 'boolean') {
        setIsCollapsed(v.collapsed);
        localStorage.setItem('pmd-nav-collapsed', String(v.collapsed));
      }
    } catch { /* malformed server value — keep boot-cache state */ }
  }, [prefsHydrated, getPref]);

  useEffect(() => {
    if (!navPrefAdopted.current) return;
    const next = JSON.stringify({ expanded: expandedItems, collapsed: isCollapsed });
    if (next !== getPref('general.sidebarNavState')) setPref('general.sidebarNavState', next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedItems, isCollapsed]);

  // Persist nav scroll position across route changes. The sidebar unmounts when
  // React Router swaps AdminLayout <-> AppContent (crossing between /:dbSlug/* and
  // /admin|/dev-tool routes), which resets scrollTop. Restore on mount and save on
  // scroll so users who scrolled deep into the tree don't lose their place.
  useLayoutEffect(() => {
    const stored = sessionStorage.getItem('pmd-nav-scrolltop');
    if (stored && navRef.current) {
      const v = parseInt(stored, 10);
      if (!Number.isNaN(v)) navRef.current.scrollTop = v;
    }
    const nav = navRef.current;
    if (!nav) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        sessionStorage.setItem('pmd-nav-scrolltop', String(nav.scrollTop));
        raf = null;
      });
    };
    nav.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      nav.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Track whether the active nav item is visible in the scroll area
  useEffect(() => {
    const el = itemRefs.current[currentPage];
    if (!el || !navRef.current) { setIsCurrentVisible(false); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setIsCurrentVisible(entry.isIntersecting),
      { root: navRef.current, threshold: 1.0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentPage, expandedItems]);

  // Dynamic reports — fetched from /api/report/configs, grouped by section,
  // injected into nav under matching reports-{section} nodes at render time.
  // Backend server-filters this list to the caller's reports.* perms.
  const [dynamicReports, setDynamicReports] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await reportingApi.listConfigs();
        if (cancelled) return;
        const data = Array.isArray(res?.data) ? res.data : [];
        setDynamicReports(data);
      } catch {
        // If the endpoint fails (unauth, network, etc.) fall back to empty
        // — Reports section will render just its section headers, which is
        // fine; better than blocking the whole sidebar on a fetch error.
        if (!cancelled) setDynamicReports([]);
      }
    })();
    return () => { cancelled = true; };
  }, [dbSlug]);

  // Filter navigation items based on AppSec permissions
  const filterNavItems = useMemo(() => {
    const perm = (key) => hasPermission(key, dbSlug);

    // Group dynamic reports by section for injection into reports-{section}.
    const reportsBySection = dynamicReports.reduce((acc, r) => {
      const key = (r.section || 'general').toLowerCase();
      if (!acc[key]) acc[key] = [];
      acc[key].push(r);
      return acc;
    }, {});

    // Helpers for "has any <prefix>.* perm" checks — mirror the pattern in
    // IPermissionChecker.HasAnyReportsPermission on the backend. Nav
    // visibility for section headers (Reports, Integration, PM) uses these.
    //
    // permAcrossDbs() checks the user's ENTIRE permissions map, not just
    // the current DB slug. Nav visibility is a "could this user access
    // this from SOMEWHERE?" check — the actual API call at click time
    // still enforces per-DB. Impersonation preview also needs this
    // (the impersonated user may have grants on a different DB than
    // the SysAdmin was viewing when they started impersonating).
    const permAcrossDbs = (key) => {
      if (isSysAdmin) return true;
      if (!currentUser?.permissions) return false;
      return Object.values(currentUser.permissions).some(list => Array.isArray(list) && list.includes(key));
    };
    const hasAnyReportsPerm = () =>
      permAcrossDbs('reports.general') || permAcrossDbs('reports.submittals') || permAcrossDbs('reports.networking')
      || permAcrossDbs('reports.quality') || permAcrossDbs('reports.commissioning') || permAcrossDbs('reports.design')
      || permAcrossDbs('reports.dynamic');
    const hasAnyPmPerm = () =>
      permAcrossDbs('projectmanagement.finance') || permAcrossDbs('projectmanagement.procurement')
      || permAcrossDbs('projectmanagement.transmittals');
    const hasAnyIntegrationPerm = () =>
      permAcrossDbs('integration.udtcomment') || permAcrossDbs('integration.scadatagen')
      || permAcrossDbs('integration.simtagen') || permAcrossDbs('integration.iobuffer')
      || permAcrossDbs('integration.plcplatforms') || permAcrossDbs('integration.tagxref');
    // Engineering: any engineering.* owner key OR the supportingdoc.view
    // alias. Post-a20 rework: Guest role gets the 5 non-supportingdoc
    // engineering keys explicitly via DefRole; view-only supportingdoc
    // is a separate grant.
    const hasAnyEngineeringPerm = () =>
      permAcrossDbs('engineering.assets') || permAcrossDbs('engineering.drawings')
      || permAcrossDbs('engineering.networking') || permAcrossDbs('engineering.powerdist')
      || permAcrossDbs('engineering.soo') || permAcrossDbs('engineering.supportingdoc')
      || permAcrossDbs('engineering.supportingdoc.view');

    const filterItem = (item) => {
      // Reports section headers — apply dynamic injection BEFORE any
      // SysAdmin bypass. If we let SysAdmin short-circuit above this
      // check, the section header returns as-is with its empty
      // navigationConfig children[] and none of the fetched reports get
      // injected. Result: only OAT (the one hardcoded child) shows.
      //
      // This branch handles the section-header perm check AND the
      // hardcoded + dynamic children merge for both SysAdmin and
      // regular users.
      if (item.id && item.id.startsWith('reports-')) {
        const section = item.id.substring('reports-'.length);
        if (!isSysAdmin && !permAcrossDbs(`reports.${section}`)) return null;

        const dynamicChildren = (reportsBySection[section] || []).map(r => ({
          id: r.slug,
          label: r.name || r.slug,
          destination: `reports/${r.slug}`,
          future: false
        }));
        const hardcodedChildren = Array.isArray(item.children) ? item.children : [];
        const merged = [...hardcodedChildren, ...dynamicChildren]
          .map(filterItem)
          .filter(Boolean);

        if (merged.length === 0) return null;
        return { ...item, children: merged };
      }

      // SysAdmin sees everything else
      if (isSysAdmin) {
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Engineering: visible if user has any engineering.* owner key
      // (or the supportingdoc.view alias). Guest DefRole grants the 5
      // non-supportingdoc engineering keys — that's the DB-access baseline.
      // Users without any engineering grant see no engineering nav.
      if (item.id === 'engineering') {
        if (!hasAnyEngineeringPerm()) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            // supporting-docs sub-item requires .view or base
            if (child.id === 'supporting-docs'
                && !permAcrossDbs('engineering.supportingdoc')
                && !permAcrossDbs('engineering.supportingdoc.view')) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      }

      // Data Entry: filter by individual dataentry.* permissions.
      // Uses permAcrossDbs so impersonated users' grants show up even if
      // the current DB context isn't one they have grants for.
      if (item.id === 'data-entry') {
        const hasAny = Object.values(modulePermMap).some(k => permAcrossDbs(k));
        if (!hasAny) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            const permKey = modulePermMap[child.id];
            if (permKey && !permAcrossDbs(permKey)) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      }

      // Reports: visible if user has ANY reports.* section perm (User+ by
      // DefRoles for 6 sections; reports.dynamic is scaffold w/o defaults).
      // Section-header children auto-hide via their own filter when the
      // caller has no reports for that section.
      if (item.id === 'reports') {
        const hasAnyReports = hasAnyReportsPerm();
        if (!hasAnyReports) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // (Reports section header handling hoisted above — see start of filterItem.)

      // Project Management: gated by having ANY projectmanagement.* perm
      // (all subkeys are SysAdmin-only by default per Josh 2026-08-05).
      if (item.id === 'project-management') {
        if (!hasAnyPmPerm()) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Utilities: utilities.* (PowerUser+ by DefRoles)
      if (item.id === 'utilities') {
        if (!perm('utilities.bulkimport') && !perm('utilities.powerquery')) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            if (child.id === 'import-template'  && !perm('utilities.bulkimport')) return null;
            if (child.id === 'power-query'      && !perm('utilities.powerquery')) return null;
            if (child.id === 'estimate-export'  && !perm('utilities.estimateexport')) return null;
            return filterItem(child);
          }).filter(Boolean);
          if (filtered.length === 0) return null;
          return { ...item, children: filtered };
        }
        return item;
      }

      // Administration: SysAdmin, or users with admin.main globally granted
      if (item.id === 'administration') {
        if (!isSysAdmin && !hasPermission('admin.main', 'global')) return null;
        if (item.children) {
          const isProd = !!window.__config__?.IS_PROD;
          const filtered = item.children.map(child => {
            if (isProd && child.id === 'admin-database') return null;
            return filterItem(child);
          }).filter(Boolean);
          return { ...item, children: filtered };
        }
        return item;
      }

      // Integration: visible if user has any integration.* tool perm
      // (integration.integration section-gate key retired 2026-08-05 —
      // section access is implied by having any child perm). Per-child
      // filtering hides tools the user doesn't have specifically.
      if (item.id === 'integration-dashboard') {
        if (!hasAnyIntegrationPerm()) return null;
        if (item.children) {
          const filtered = item.children.map(child => {
            const childPerm = integrationPermMap[child.id];
            if (childPerm && !perm(childPerm)) return null;
            return filterItem(child);
          }).filter(Boolean);
          return { ...item, children: filtered };
        }
        return item;
      }

      // Development: gated by development.main permission (global — not DB-scoped)
      if (item.id === 'development') {
        if (!isSysAdmin && !hasPermission('development.main', 'global')) return null;
        if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
        return item;
      }

      // Default: visible to all (home, any future top-level items)
      if (item.children) return { ...item, children: item.children.map(filterItem).filter(Boolean) };
      return item;
    };

    return navigationConfig.map(filterItem).filter(Boolean);
  }, [isSysAdmin, isAdmin, dbSlug, hasPermission, dynamicReports]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.trim();
    const seen = new Set();
    return flattenNav(filterNavItems)
      .filter(item => item.label.toLowerCase().includes(q.toLowerCase()))
      .filter(item => {
        if (seen.has(item.destination)) return false;
        seen.add(item.destination);
        return true;
      });
  }, [filterNavItems, searchQuery]);

  const openSearch = () => {
    setSearchMode(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchMode(false);
    setSearchQuery('');
  };

  const handleSearchSelect = (destination) => {
    setCurrentPage(destination);
    closeSearch();
    const path = findPathToDestination(navigationConfig, destination);
    if (path) {
      const parents = path.slice(0, -1);
      setExpandedItems(prev => {
        const next = [...prev];
        parents.forEach(id => { if (!next.includes(id)) next.push(id); });
        localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
        return next;
      });
    }
    setTimeout(() => {
      const el = itemRefs.current[destination];
      if (el && navRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Auto-expand ancestor nav nodes whenever currentPage changes so the active
  // item is always visible. Only expands parents — never the item itself —
  // so the user retains manual chevron control over group expansion.
  useEffect(() => {
    if (!currentPage) return;
    const path = findPathToDestination(navigationConfig, currentPage);
    if (!path || path.length <= 1) return;
    const parents = path.slice(0, -1);
    setExpandedItems(prev => {
      const next = [...prev];
      let changed = false;
      parents.forEach(id => { if (!next.includes(id)) { next.push(id); changed = true; } });
      if (!changed) return prev;
      localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
      return next;
    });
  }, [currentPage]);

  // Auto-expand and scroll when sidebarTarget changes (FK navigation)
  useEffect(() => {
    if (sidebarTarget) {
      // Find path to target in nav tree
      const path = findPathToDestination(navigationConfig, sidebarTarget);
      
      if (path) {
        // Expand all parent items (exclude the last one which is the target itself)
        const parentsToExpand = path.slice(0, -1);
        setExpandedItems(prev => {
          const newExpanded = [...prev];
          parentsToExpand.forEach(id => {
            if (!newExpanded.includes(id)) {
              newExpanded.push(id);
            }
          });
          return newExpanded;
        });

        // Scroll to the item after a brief delay to let expansion render
        setTimeout(() => {
          const targetEl = itemRefs.current[sidebarTarget];
          if (targetEl && navRef.current) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }

      // Clear the target after handling
      if (clearSidebarTarget) {
        clearSidebarTarget();
      }
    }
  }, [sidebarTarget, clearSidebarTarget]);

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => {
      const next = prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId];
      localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
      return next;
    });
  };

  const expandAll = () => {
    const allExpandableIds = [];
    const collectIds = (items) => {
      items.forEach(item => {
        if (item.children && item.children.length > 0) {
          allExpandableIds.push(item.id);
          collectIds(item.children);
        }
      });
    };
    collectIds(navigationConfig);
    setExpandedItems(allExpandableIds);
    localStorage.setItem('pmd-nav-expanded', JSON.stringify(allExpandableIds));
  };

  const collapseAll = () => {
    setExpandedItems([]);
    localStorage.setItem('pmd-nav-expanded', JSON.stringify([]));
  };

  const allExpandableIds = useMemo(() => {
    const ids = [];
    const collect = (items) => items.forEach(item => {
      if (item.children?.length > 0) { ids.push(item.id); collect(item.children); }
    });
    collect(navigationConfig);
    return ids;
  }, []);

  const isAllExpanded = allExpandableIds.every(id => expandedItems.includes(id));
  const isAllCollapsed = expandedItems.length === 0;

  const jumpToSelected = () => {
    const path = findPathToDestination(navigationConfig, currentPage);
    if (path) {
      const parents = path.slice(0, -1);
      setExpandedItems(prev => {
        const next = [...prev];
        parents.forEach(id => { if (!next.includes(id)) next.push(id); });
        localStorage.setItem('pmd-nav-expanded', JSON.stringify(next));
        return next;
      });
    }
    setTimeout(() => {
      const el = itemRefs.current[currentPage];
      if (el && navRef.current) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // Separate Home from other nav items (using permission-filtered nav)
  const homeItem = filterNavItems.find(item => item.id === 'home');
  const otherNavItems = filterNavItems.filter(item => item.id !== 'home');

  return (
    <aside className={`stui-nav-sidebar ${isCollapsed ? 'stui-collapsed' : ''}`}>
      {/* Home row with collapse toggle */}
      {homeItem && (
        <div className="stui-nav-home-row">
          <button
            ref={el => { itemRefs.current[homeItem.destination] = el; }}
            onClick={() => setCurrentPage(homeItem.destination)}
            className={`stui-nav-item stui-nav-item-level-1 stui-nav-item-home ${currentPage === homeItem.destination ? 'active' : ''}`}
            data-tooltip={homeItem.label}
          >
            <div className="stui-nav-item-content">
              <Home size={16} className="stui-nav-item-icon" />
              {!isCollapsed && <span className="stui-nav-item-label">{homeItem.label}</span>}
            </div>
          </button>
          <button 
            className="stui-nav-collapse-toggle"
            onClick={() => { const next = !isCollapsed; setIsCollapsed(next); localStorage.setItem('pmd-nav-collapsed', String(next)); }}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
      )}
      
      {/* Scrollable nav area — tree or search results */}
      <nav className="stui-nav-scroll" ref={navRef}>
        {searchMode && searchQuery.trim() ? (
          searchResults.length === 0 ? (
            <div className="stui-nav-search-empty">No results</div>
          ) : (
            searchResults.map(item => (
              <button
                key={item.id}
                className="stui-nav-search-item"
                onClick={() => handleSearchSelect(item.destination)}
              >
                <span className="stui-nav-search-label">{highlightMatch(item.label, searchQuery)}</span>
                {item.breadcrumb.length > 0 && (
                  <span className="stui-nav-search-crumb">{item.breadcrumb.join(' / ')}</span>
                )}
              </button>
            ))
          )
        ) : (
          otherNavItems.map(item => (
            <NavItem
              key={item.id}
              item={item}
              level={1}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              expandedItems={expandedItems}
              toggleExpand={toggleExpand}
              isCollapsed={isCollapsed}
              itemRefs={itemRefs}
            />
          ))
        )}
      </nav>

      {!isCollapsed && (
        <div className="stui-nav-footer">
          {searchMode ? (
            <>
              <input
                ref={searchInputRef}
                className="stui-nav-search-input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') closeSearch(); }}
                placeholder="Search navigation..."
              />
              <button onClick={closeSearch} className="stui-nav-footer-btn stui-nav-search-close" title="Close search">
                <X size={15} />
              </button>
            </>
          ) : (
            <>
              <button onClick={openSearch} className="stui-nav-footer-btn" title="Search navigation">
                <Search size={15} />
              </button>
              <button onClick={jumpToSelected} className={`stui-nav-footer-btn${isCurrentVisible ? ' disabled' : ''}`} title="Show current location" disabled={isCurrentVisible}>
                <Navigation size={15} />
              </button>
              <button onClick={expandAll} className={`stui-nav-footer-btn${isAllExpanded ? ' disabled' : ''}`} title="Expand all" disabled={isAllExpanded}>
                <ArrowDownNarrowWide size={15} />
              </button>
              <button onClick={collapseAll} className={`stui-nav-footer-btn${isAllCollapsed ? ' disabled' : ''}`} title="Collapse all" disabled={isAllCollapsed}>
                <ArrowUpNarrowWide size={15} />
              </button>
            </>
          )}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
